<!-- Allocation and currencies: portfolio structure, allocation and currency exposure over time, rebalancing to target. -->
<script lang="ts">
  import * as E from "../core/engine";
  import { app, need, rangeOf } from "../lib/state.svelte";
  import { saveConfig, toast } from "../lib/actions";
  import { esc, fmtDate, fmtNum, pctPlain } from "../lib/format";
  import { baseOption, compactMoney, palette, series, timeAxis, tipMoney, tooltipRows, valueAxis, type TipParam } from "../lib/chart";
  import ViewHead from "../components/ViewHead.svelte";
  import Card from "../components/Card.svelte";
  import Chart from "../components/Chart.svelte";
  import Seg from "../components/Seg.svelte";
  import Money from "../components/Money.svelte";
  import Signed from "../components/Signed.svelte";

  const DIMS = [["group", "Grupa"], ["assetClass", "Klasa aktywów"], ["instrument", "Instrument"], ["currency", "Waluta notowania"], ["broker", "Broker"], ["account", "Rachunek"]] as const;
  type Dim = (typeof DIMS)[number][0];
  const FX_MODES = [["pct", "Udział %"], ["abs", "Kwota"]] as const;

  const m = $derived(need(app.model));
  const A = $derived(need(app.agg));
  const r = $derived(app.range);
  const e = $derived(m.idx(r.end));
  const hold = $derived(E.holdings(m, app.accList, r.start, r.end));
  const dim = $derived((app.ui.allocDim || "group") as Dim);
  const al = $derived(E.allocation(m, hold, A, e, dim));
  const tot = $derived(al.reduce((s, x) => s + x.value, 0));
  const targets = $derived(need(app.data).config.targets || {});
  const groupsAl = $derived(E.allocation(m, hold, A, e, "group").filter((x) => x.name !== "Gotówka"));
  const extra = $derived(+(app.ui.rebExtra || 0));
  const hasT = $derived(Object.keys(targets).length > 0);
  const reb = $derived(E.rebalance(groupsAl, hasT ? targets : Object.fromEntries(groupsAl.map((g) => [g.name, 0])), extra));
  const fxMode = $derived(app.ui.fxMode || "pct");

  // targets entered in the table (saved with a button); after any view change they revert to the saved values
  const editToken = $derived({ targets, extra, dim, fxMode });
  let edits = $state.raw<{ token: object | null; vals: Record<string, string> }>({ token: null, vals: {} });
  const curEdits = $derived(edits.token === editToken ? edits.vals : null);
  const tgtVal = (name: string): string => curEdits?.[name] ?? String(targets[name] ?? "");
  const tgtSum = $derived(curEdits ? reb.reduce((s, x) => s + (+tgtVal(x.name) || 0), 0) : Object.values(targets).reduce((s, x) => s + (+x || 0), 0));
  function onTgt(name: string, v: string) {
    const vals = curEdits ? { ...curEdits } : Object.fromEntries(reb.map((x) => [x.name, tgtVal(x.name)]));
    vals[name] = v;
    edits = { token: editToken, vals };
  }
  async function save() {
    const t: Record<string, number> = {};
    for (const x of reb) { const v = tgtVal(x.name); if (v !== "") t[x.name] = +v; }
    const cfg2 = { ...need(app.data).config, targets: t };
    await saveConfig(cfg2);
    toast("Zapisano cele alokacji");
  }

  const pieOpt = $derived.by(() => {
    const p = palette();
    const pieData = al.filter((x) => x.value > 0);
    const top = pieData.slice(0, 6);
    const rest = pieData.slice(6).reduce((s, x) => s + x.value, 0);
    if (rest > 0) top.push({ name: "Pozostałe", value: rest });
    return { ...baseOption(p), legend: { show: false },
      tooltip: { trigger: "item", backgroundColor: p.surface, borderColor: p.border, textStyle: { color: p.ink }, formatter: (x: TipParam) => `${esc(x.name)}<br><b>${tipMoney(x.value)}</b> (${fmtNum(x.percent, 1)}%)` },
      series: [{ type: "pie", radius: ["55%", "82%"], padAngle: 1, itemStyle: { borderRadius: 4, borderColor: p.surface, borderWidth: 2 }, label: { show: false },
        data: top.map((x, k) => ({ ...x, itemStyle: { color: series(k) } })) }] };
  });

  // weekly samples from the start of history to the end of the period
  const i0 = $derived(m.idx(rangeOf("MAX").start));
  const sampleIdx = $derived.by(() => {
    const out: number[] = [];
    for (let i = i0; i <= e; i += i + 7 > e && i !== e ? e - i : 7) {
      out.push(i);
      if (i === e) break;
    }
    return out;
  });
  const groups = $derived([...new Set(Object.values(m.instr).map((x) => x.group))]);

  const allocTimeOpt = $derived.by(() => {
    const p = palette();
    const pts: Record<string, [string, number][]> = {};
    groups.forEach((g) => (pts[g] = []));
    for (const i of sampleIdx) {
      const byG: Record<string, number> = {};
      let t = 0;
      for (const a of app.accList) {
        const Ac = m.acc[a];
        for (const [s, q] of Object.entries(Ac.qty)) { if (!q[i]) continue; const v = q[i] * (m.pPLN(s, i) || 0); byG[m.instr[s].group] = (byG[m.instr[s].group] || 0) + v; t += v; }
      }
      groups.forEach((g) => pts[g].push([m.days[i], t > 0 ? (byG[g] || 0) / t : 0]));
    }
    const order = groupsAl.map((g) => g.name).concat(groups.filter((g) => !groupsAl.some((x) => x.name === g)));
    return { ...baseOption(p), xAxis: timeAxis(p), yAxis: valueAxis((v) => fmtNum(v * 100, 0) + "%", p, { max: 1 }),
      tooltip: { ...baseOption(p).tooltip, formatter: (ps: TipParam) => tooltipRows(ps.filter((x: TipParam) => x.value[1] > 0.0005), (v) => pctPlain(v).replace("+", "")) },
      series: order.filter((g) => pts[g].some((x) => x[1] > 0)).map((g, k) => ({ name: g, type: "line", stack: "a", data: pts[g], showSymbol: false, lineStyle: { width: 0 }, areaStyle: { color: series(k), opacity: 0.85 }, itemStyle: { color: series(k) } })) };
  });

  const fxTimeOpt = $derived.by(() => {
    const p = palette();
    const mode = fxMode;
    const samples: { d: string; byC: Record<string, number>; t: number }[] = [];
    const allC = new Set<string>();
    for (const i of sampleIdx) {
      const byC: Record<string, number> = {};
      let t = 0;
      for (const a of app.accList) {
        const Ac = m.acc[a];
        for (const [s, q] of Object.entries(Ac.qty)) { if (!q[i]) continue; const v = q[i] * (m.pPLN(s, i) || 0); const c = m.priceCcy[s] || "PLN"; byC[c] = (byC[c] || 0) + v; t += v; }
        for (const [c, q] of Object.entries(Ac.cashCcy)) { const v = q[i] * (m.fx(c, i) || 0); if (Math.abs(v) < 0.5) continue; byC[c] = (byC[c] || 0) + v; t += v; }
      }
      Object.keys(byC).forEach((c) => allC.add(c));
      samples.push({ d: m.days[i], byC, t });
    }
    const fxPts: Record<string, [string, number][]> = {};
    for (const c of allC) fxPts[c] = samples.map((x) => [x.d, mode === "pct" ? (x.t > 0 ? Math.max(0, x.byC[c] || 0) / x.t : 0) : x.byC[c] || 0]);
    const cOrder = Object.keys(fxPts).sort((x, y) => (fxPts[y].at(-1)?.[1] || 0) - (fxPts[x].at(-1)?.[1] || 0));
    const fxColor: Record<string, string> = { PLN: series(0), EUR: series(1), USD: series(2), GBP: series(3) };
    return { ...baseOption(p), xAxis: timeAxis(p),
      yAxis: mode === "pct" ? valueAxis((v) => fmtNum(v * 100, 0) + "%", p, { max: 1 }) : valueAxis(compactMoney, p),
      tooltip: { ...baseOption(p).tooltip, formatter: (ps: TipParam) => tooltipRows(ps.filter((x: TipParam) => Math.abs(x.value[1]) > 0.0005), (v) => (mode === "pct" ? pctPlain(v).replace("+", "") : tipMoney(v))) },
      series: cOrder.map((c, k) => ({ name: c, type: "line", stack: "fx", data: fxPts[c].map(([d, v]) => [d, v]), showSymbol: false, lineStyle: { width: 0 }, areaStyle: { color: fxColor[c] || series(k + 4), opacity: 0.85 }, itemStyle: { color: fxColor[c] || series(k + 4) } })) };
  });
</script>

<ViewHead title="Alokacja i waluty" sub="stan na {fmtDate(r.end)}" />
<div class="grid g-2-1">
  <Card title="Struktura portfela">
    {#snippet extra()}<Seg id="dimSeg" attr="d" options={DIMS} value={dim} onselect={(k) => (app.ui.allocDim = k)} />{/snippet}
    <div class="legend-list">
      {#each al as x, k (k)}
        <div><div class="legend-row"><span><span class="dot" style="background:{series(k)}"></span>{x.name}</span><span class="money">{fmtNum(x.value)} zł</span><b>{fmtNum((x.value / tot) * 100, 1)}%</b></div><div class="meter"><div style="width:{Math.max(0, (x.value / tot) * 100)}%;background:{series(k)}"></div></div></div>
      {/each}
    </div>
  </Card>
  <Card title="Udział"><Chart id="chPie" option={pieOpt} /></Card>
</div>
<Card title="Alokacja w czasie (wg grup)" hint="udział grup w wartości papierów"><Chart id="chAllocTime" option={allocTimeOpt} /></Card>
<Card title="Ekspozycja walutowa w czasie" hint="waluta notowania papierów i gotówki na rachunkach (obligacje skarbowe = PLN); uwaga: ETF na MSCI World notowany w EUR i tak zależy głównie od dolara">
  {#snippet extra()}<Seg id="fxModeSeg" attr="m" options={FX_MODES} value={fxMode} onselect={(k) => (app.ui.fxMode = k)} />{/snippet}
  <Chart id="chFxTime" option={fxTimeOpt} />
</Card>
<Card title="Rebalansowanie do alokacji docelowej">
  <p class="small muted">Wpisz docelowe udziały grup (grupy ustawiasz w Import i dane → Instrumenty). Plan „tylko dokupy” rozdziela nową wpłatę na grupy poniżej celu — bez sprzedaży i bez podatku.</p>
  <div class="row" style="margin-bottom:10px"><label>Nowa wpłata do rozdzielenia: <input type="number" class="num" id="rebExtra" value={extra || ""} placeholder="0" style="width:110px" onchange={(ev) => (app.ui.rebExtra = +ev.currentTarget.value || 0)} /> zł</label><span class="spacer"></span><button class="btn primary" id="saveTargets" onclick={save}>Zapisz cele</button></div>
  <div class="table-wrap"><table><thead><tr><th class="l">Grupa</th><th>Wartość</th><th>Udział</th><th>Cel %</th><th>Wartość docelowa</th><th>Różnica</th><th>Dokup (plan)</th></tr></thead>
    <tbody>
      {#each reb as x (x.name)}
        <tr><td class="l">{x.name}</td><td><Money v={x.value} /></td><td>{fmtNum(x.current * 100, 1)}%</td><td><input type="number" class="num tgt" data-g={x.name} value={tgtVal(x.name)} step="1" min="0" oninput={(ev) => onTgt(x.name, ev.currentTarget.value)} /></td>
          <td>{#if hasT}<Money v={x.targetValue} />{:else}–{/if}</td><td>{#if hasT}<Signed v={x.diff} />{:else}–{/if}</td><td>{#if x.buy > 0.5}<b><Money v={x.buy} /></b>{:else}–{/if}</td></tr>
      {/each}
      <tr class="total"><td class="l">Razem</td><td><Money v={groupsAl.reduce((s, x) => s + x.value, 0)} /></td><td></td><td id="tgtSum">{fmtNum(tgtSum, 0)}%</td><td></td><td></td><td><Money v={reb.reduce((s, x) => s + x.buy, 0)} /></td></tr>
    </tbody>
  </table></div>
</Card>
